import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getExReady, RegisterImportPool, IImportPoolRegistry } from "./Base";

import "./LinoCodex.css";

const exModulePromises: IImportPoolRegistry = RegisterImportPool({
    "quagga2": import(/* webpackChunkName: "quagga2_LinoCodex" */"@ericblade/quagga2"),
});

const { Lino } = window;


export function BarcodeScanner({ onDetected, onError }) {
    const [Quagga, setQuagga] = React.useState(null);
    const localEx = getExReady(exModulePromises.modulePromises, ["quagga2"], (mods) => {
        mods.Quagga = mods.quagga2.default;
        setQuagga(mods.Quagga);
    });
    const scannerRef = useRef(null);

    const handleDetected = useCallback((result) => {
        if (result.codeResult) {
            onDetected({
                code: result.codeResult.code,
                format: result.codeResult.format,
            });
        }
    }, [onDetected]);

    useEffect(() => {
        if (!Quagga) {
            console.error('Quagga is not loaded yet');
            return;
        }
        localEx.Quagga.init({
            inputStream: {
                type: 'LiveStream',
                target: scannerRef.current,
                constraints: {
                    facingMode: 'environment'
                }
            },
            decoder: {
                readers: [
                    'code_128_reader',
                    'ean_reader',
                    'ean_8_reader',
                    'code_39_reader',
                    'code_39_vin_reader',
                    'codabar_reader',
                    'upc_reader',
                    'upc_e_reader',
                    'i2of5_reader',
                    '2of5_reader',
                    'code_93_reader',
                    'code_32_reader',
                    'pharmacode_reader',
                ]
            }
        }, (err) => {
            if (err) {
                console.error('Failed to initialize:', err);
                onError();
                return;
            }
            localEx.Quagga.start();
        });

        localEx.Quagga.onDetected(handleDetected);

        return () => {
            localEx.Quagga.offDetected(handleDetected);
            localEx.Quagga.stop();
        };
    }, [handleDetected, Quagga]);

    return Quagga && <div ref={scannerRef}/>;
}

BarcodeScanner.propTypes = {
    onDetected: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
}


export function PrinterProgress({ totalCount }) {
    const [cancelDisabled, setCancelDisabled] = React.useState(false);
    const [progress, setProgress] = React.useState("0%");
    const [progressStatus, setProgressStatus] = React.useState(
        `Put 0 copies of ${totalCount} to printer queue`);

    useEffect(() => {
        const handleProgress = (data) => {
            setProgressStatus(`Put ${data.current} copies of ${totalCount} to printer queue`);
            const percentage = (data.current / totalCount) * 100;
            setProgress(`${percentage}%`);
        };

        window.printer?.onProgress(handleProgress);

        return () => {
            window.printer?.removeProgressListeners();
        };
    }, []);

    if (!progress) {
        return null;
    }

    return (
        <div id="loadingMask" className="loading-overlay" style={{ display: 'flex' }}>
            <div className="loading-card">
                <div className="spinner"></div>
                <h4>Printing in Progress...</h4>
                <p id="progressStatus">{progressStatus}</p>
                <div className="progress-bar-container">
                    <div id="progressBarFill" className="progress-fill" style={{width: progress}}/>
                </div>
                <button
                    className="btn-cancel"
                    disabled={cancelDisabled}
                    id="cancelPrintBtn"
                    onClick={async () => {
                        setCancelDisabled(true);
                        await window.printer?.cancelPrintJob();
                    }}>Cancel Printing</button>
            </div>
        </div>
    );
}

PrinterProgress.propTypes = {
    totalCount: PropTypes.number.isRequired,
};


export default BarcodeScanner;
